import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Card, Button, Space, Modal, Form, Input, InputNumber,
    Select, DatePicker, Typography, message, Popconfirm, Tag,
    Row, Col, Drawer, Descriptions, Divider, Badge, Empty, Tooltip, Tabs
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    FileSearchOutlined, SearchOutlined, FilterOutlined,
    DollarOutlined, ReloadOutlined, EyeOutlined,
    CalendarOutlined, SyncOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { formatMoneyJOD, formatDate } from '../utils/formatters';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Expenses = () => {
    const [expenses, setExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [activeTab, setActiveTab] = useState('random');
    const [form] = Form.useForm();
    const [filterForm] = Form.useForm();

    // Filters
    const [filters, setFilters] = useState({
        dates: null,
        categoryId: null,
        user: '',
        minAmount: null,
        maxAmount: null,
        search: ''
    });

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            let params = new URLSearchParams();
            params.append('expense_type', activeTab);
            
            if (filters.dates) {
                params.append('startDate', filters.dates[0].format('YYYY-MM-DD'));
                params.append('endDate', filters.dates[1].format('YYYY-MM-DD'));
            }
            if (filters.categoryId) params.append('categoryId', filters.categoryId);
            if (filters.user) params.append('user', filters.user);
            if (filters.minAmount) params.append('minAmount', filters.minAmount);
            if (filters.maxAmount) params.append('maxAmount', filters.maxAmount);
            if (filters.search) params.append('search', filters.search);

            const [expRes, catRes] = await Promise.all([
                axios.get(`/api/accounting/expenses?${params.toString()}`, { headers }),
                axios.get('/api/accounting/categories', { headers })
            ]);

            setExpenses(expRes.data);
            setCategories(catRes.data);
        } catch (error) {
            message.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [filters, activeTab]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchData();
        }, 300);
        return () => clearTimeout(timer);
    }, [fetchData]);

    const showModal = (expense = null) => {
        setEditingExpense(expense);
        if (expense) {
            form.setFieldsValue({
                ...expense,
                date: dayjs(expense.date),
                due_date: expense.due_date ? dayjs(expense.due_date) : null,
                next_due_date: expense.next_due_date ? dayjs(expense.next_due_date) : null,
            });
        } else {
            form.resetFields();
            form.setFieldsValue({ 
                date: dayjs(),
                expense_type: activeTab 
            });
        }
        setIsModalVisible(true);
    };

    const showDrawer = (record) => {
        setSelectedExpense(record);
        setIsDrawerVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingExpense(null);
    };

    const onFinish = async (values) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const payload = {
                ...values,
                date: values.date.format('YYYY-MM-DD'),
                due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
                next_due_date: values.next_due_date ? values.next_due_date.format('YYYY-MM-DD') : null,
            };

            if (editingExpense) {
                await axios.put(`/api/accounting/expenses/${editingExpense.id}`, payload, { headers });
                message.success('Expense record updated successfully');
            } else {
                await axios.post('/api/accounting/expenses', payload, { headers });
                message.success('New expense added successfully');
            }
            setIsModalVisible(false);
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to save expense');
        }
    };

    const handleDelete = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            await axios.delete(`/api/accounting/expenses/${id}`, { headers });
            message.success('Expense record deleted');
            if (selectedExpense && selectedExpense.id === id) setIsDrawerVisible(false);
            fetchData();
        } catch (error) {
            message.error('Failed to delete expense');
        }
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => <Text>{formatDate(date)}</Text>,
            sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
            width: 120,
        },
        {
            title: 'Category',
            dataIndex: 'category_name',
            key: 'category_name',
            render: (text, record) => (
                <Tag color={record.category_color} style={{ borderRadius: '4px' }}>{text}</Tag>
            ),
            width: 150,
        },
        {
            title: 'Note / Reference',
            dataIndex: 'note',
            key: 'note',
            ellipsis: true,
        },
        ...(activeTab === 'recurring' ? [
            {
                title: 'Repeat Cycle',
                dataIndex: 'repeat_cycle',
                key: 'repeat_cycle',
                render: (val) => val ? <Tag icon={<SyncOutlined spin={false} />} color="blue">{val.toUpperCase()}</Tag> : '-',
                width: 130,
            },
            {
                title: 'Next Due',
                dataIndex: 'next_due_date',
                key: 'next_due_date',
                render: (date) => date ? <Text type="warning"><CalendarOutlined /> {formatDate(date)}</Text> : '-',
                width: 130,
            }
        ] : []),
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (val) => <Text strong style={{ color: '#cf1322' }}>{formatMoneyJOD(val)}</Text>,
            sorter: (a, b) => a.amount - b.amount,
            width: 140,
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button icon={<EyeOutlined />} size="small" onClick={() => showDrawer(record)} />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button icon={<EditOutlined />} size="small" onClick={() => showModal(record)} />
                    </Tooltip>
                    <Popconfirm title="Permanently delete this expense?" onConfirm={() => handleDelete(record.id)} okText="Delete" okType="danger">
                        <Button danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            ),
            width: 140,
        },
    ];

    const tabItems = [
        {
            key: 'random',
            label: (
                <span>
                    <DollarOutlined />
                    Daily / Random Expenses
                </span>
            ),
        },
        {
            key: 'recurring',
            label: (
                <span>
                    <CalendarOutlined />
                    Commitments / Fixed Expenses
                </span>
            ),
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2} style={{ margin: 0 }}>Accounting Expenses</Title>
                    <Text type="secondary">Manage operational and fixed business costs</Text>
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={fetchData}>Refresh Data</Button>
                    <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => showModal()}>
                        Record New Expense
                    </Button>
                </Space>
            </div>

            <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab} 
                items={tabItems} 
                className="premium-tabs"
                style={{ marginBottom: 24 }}
            />

            <Card className="premium-card shadow-sm" bodyStyle={{ padding: 0 }}>
                {/* Advanced Filter Bar */}
                <div style={{ padding: '20px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6} lg={5}>
                            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600 }}>DATE RANGE</Text>
                            <RangePicker
                                style={{ width: '100%', marginTop: 4 }}
                                onChange={(val) => setFilters(prev => ({ ...prev, dates: val }))}
                                value={filters.dates}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={4}>
                            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600 }}>CATEGORY</Text>
                            <Select
                                style={{ width: '100%', marginTop: 4 }}
                                placeholder="All Categories"
                                allowClear
                                onChange={(val) => setFilters(prev => ({ ...prev, categoryId: val }))}
                                value={filters.categoryId}
                            >
                                {categories.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={5}>
                            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600 }}>KEYWORDS</Text>
                            <Input
                                style={{ marginTop: 4 }}
                                placeholder="Search notes..."
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                value={filters.search}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6} lg={5}>
                            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600 }}>AMOUNT RANGE</Text>
                            <Space style={{ width: '100%', marginTop: 4 }}>
                                <InputNumber
                                    placeholder="Min"
                                    style={{ width: '100%' }}
                                    onChange={(val) => setFilters(prev => ({ ...prev, minAmount: val }))}
                                    value={filters.minAmount}
                                />
                                <InputNumber
                                    placeholder="Max"
                                    style={{ width: '100%' }}
                                    onChange={(val) => setFilters(prev => ({ ...prev, maxAmount: val }))}
                                    value={filters.maxAmount}
                                />
                            </Space>
                        </Col>
                        <Col xs={24} lg={5} style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <Button
                                block
                                danger={Object.values(filters).some(v => v !== null && v !== '')}
                                icon={<FilterOutlined />}
                                onClick={() => setFilters({
                                    dates: null,
                                    categoryId: null,
                                    user: '',
                                    minAmount: null,
                                    maxAmount: null,
                                    search: ''
                                })}
                            >
                                Clear All Filters
                            </Button>
                        </Col>
                    </Row>
                </div>

                <Table
                    columns={columns}
                    dataSource={expenses}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} expenses`
                    }}
                    size="large"
                    onRow={(record) => ({
                        onDoubleClick: () => showDrawer(record),
                    })}
                />
            </Card>

            {/* Detail Drawer */}
            <Drawer
                title="Expense Details"
                width={550}
                onClose={() => setIsDrawerVisible(false)}
                open={isDrawerVisible}
                extra={
                    <Space>
                        <Button onClick={() => { setIsDrawerVisible(false); showModal(selectedExpense); }}>Edit</Button>
                        <Button type="primary" onClick={() => setIsDrawerVisible(false)}>Close</Button>
                    </Space>
                }
            >
                {selectedExpense ? (
                    <>
                        <Descriptions bordered column={1} labelStyle={{ fontWeight: 600, width: '140px' }}>
                            <Descriptions.Item label="Classification">
                                <Tag color={selectedExpense.expense_type === 'recurring' ? 'purple' : 'blue'}>
                                    {selectedExpense.expense_type === 'recurring' ? 'Fixed / Commitment' : 'Daily / Random'}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Date">{formatDate(selectedExpense.date)}</Descriptions.Item>
                            <Descriptions.Item label="Category">
                                <Tag color={selectedExpense.category_color}>{selectedExpense.category_name}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Amount">
                                <Text strong style={{ color: '#cf1322', fontSize: '20px' }}>{formatMoneyJOD(selectedExpense.amount)}</Text>
                            </Descriptions.Item>
                            {selectedExpense.expense_type === 'recurring' && (
                                <>
                                    <Descriptions.Item label="Repeat Cycle">{selectedExpense.repeat_cycle || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Due Date">{selectedExpense.due_date ? formatDate(selectedExpense.due_date) : 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Next Due Date">{selectedExpense.next_due_date ? formatDate(selectedExpense.next_due_date) : 'N/A'}</Descriptions.Item>
                                </>
                            )}
                            <Descriptions.Item label="Created By">{selectedExpense.created_by}</Descriptions.Item>
                            <Descriptions.Item label="Timestamp">{dayjs(selectedExpense.created_at).format('DD MMM YYYY, HH:mm')}</Descriptions.Item>
                        </Descriptions>
                        <Divider orientation="left">Notes / Reference</Divider>
                        <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '8px', minHeight: '100px', border: '1px solid #f0f0f0' }}>
                            <Text>{selectedExpense.note || <Text type="secondary" italic>No notes provided</Text>}</Text>
                        </div>
                    </>
                ) : <Empty />}
            </Drawer>

            {/* Upsert Modal */}
            <Modal
                title={
                    <Space>
                        {editingExpense ? <EditOutlined /> : <PlusOutlined />}
                        <span>{editingExpense ? 'Modify Expense Record' : 'Record New Expense'}</span>
                    </Space>
                }
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                destroyOnClose
                width={650}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{ 
                        date: dayjs(),
                        expense_type: activeTab
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="expense_type"
                                label="Expense Classification"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Option value="random">Daily / Random Expense</Option>
                                    <Option value="recurring">Fixed / Commitment</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="date"
                                label="Transaction Date"
                                rules={[{ required: true, message: 'Select transaction date' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="category_id"
                                label="Expense Category"
                                rules={[{ required: true, message: 'Assignment is required' }]}
                            >
                                <Select placeholder="Assign to category">
                                    {categories.map(cat => (
                                        <Option key={cat.id} value={cat.id}>
                                            <Badge color={cat.color} text={cat.name} />
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="amount"
                                label="Amount (JOD)"
                                rules={[
                                    { required: true, message: 'Enter exact amount' },
                                    { type: 'number', min: 0.001, message: 'Value must be positive' }
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    precision={3}
                                    placeholder="0.000"
                                    step={0.1}
                                    addonAfter="JOD"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Conditional Fields for Recurring */}
                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.expense_type !== currentValues.expense_type}>
                        {({ getFieldValue }) => 
                            getFieldValue('expense_type') === 'recurring' ? (
                                <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '24px', border: '1px dashed #d9d9d9' }}>
                                    <Title level={5} style={{ marginTop: 0, marginBottom: 16, fontSize: '14px' }}>Commitment Details</Title>
                                    <Row gutter={16}>
                                        <Col span={8}>
                                            <Form.Item name="repeat_cycle" label="Repeat Cycle">
                                                <Select placeholder="Select cycle">
                                                    <Option value="monthly">Monthly</Option>
                                                    <Option value="weekly">Weekly</Option>
                                                    <Option value="yearly">Yearly</Option>
                                                    <Option value="quarterly">Quarterly</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item name="due_date" label="Due Date">
                                                <DatePicker style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item name="next_due_date" label="Next Due Date">
                                                <DatePicker style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </div>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item
                        name="note"
                        label="Notes / Reference / Description"
                    >
                        <Input.TextArea rows={4} placeholder="Detailed purpose of this expense..." />
                    </Form.Item>

                    <Divider />

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space size="middle">
                            <Button onClick={handleCancel} size="large">Cancel</Button>
                            <Button type="primary" htmlType="submit" icon={<DollarOutlined />} size="large">
                                {editingExpense ? 'Update Expense' : 'Record Expense'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Expenses;
